import React, { CSSProperties, useEffect, useState, ChangeEvent, ReactElement, useContext } from "react";
import { BaseType, IColumn, IEditionResponse, IState, IUser, Order, ValueAccessor } from "../../../../types";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip, useTheme
} from "@mui/material";
import { cloneObject, stableSort } from "../../../../system/utils";
import StyledTableRow from "../../../../components/styledTableRow";
import TablePaginationActions from "../../../../components/tablePaginationActions";
import { IDivision, ITask, ITaskAttachment, PriorityType, ProgressType } from "../types";
import { useAsyncFetch, useFetch } from "../../../../system/useFetch";
import IconButton from "@mui/material/IconButton";
import { Add, Delete, FilterList } from "@mui/icons-material";
import TaskEditor from "./taskEditor";
import { DateTime } from "luxon";
import { addAlert } from "../../../../actions";
import { useDispatch, useSelector } from "react-redux";
import {
  getPriorityColor,
  getPriorityText,
  getProgressColor,
  getProgressText,
  PriorityIcon,
  ProgressIcon
} from "../utils";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CommentIcon from "@mui/icons-material/Comment";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import TabPanel from "../../../../components/tabPanel";
import { IPieConfig, ISeries } from "../../../../components/charts/types";
import PieChart from "../../../../components/charts/pieChart";
import { getPalette } from "../../../../configuration/configuration";
import JSZip from "jszip";
import TaskComments from "./taskComments";
import FilterDialog from "./filterDialog";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SystemContext } from "../../../../context/systemContext";


interface IProps {
  hash: string;
  user: IUser;
  division: IDivision;
}

interface IOption {
  value: any;
  name: string;
}

interface IOrderConfig {
  order: Order;
  orderBy: keyof ITask;
  accessor: ValueAccessor;
}

const progressFilter = [
  { value: 0, name: "Pending" },
  { value: 1, name: "In Progress" },
  { value: 2, name: "On Hold" },
  { value: 3, name: "Cancelled" },
  { value: 4, name: "Completed" }
];

const priorityFilter = [
  { value: 0, name: "Low" },
  { value: 1, name: "Medium" },
  { value: 2, name: "Important" },
  { value: 3, name: "Urgent" }
];

const columns: IColumn[] = [

  { id: "index", label: "", minWidth: 80 },
  { id: "reference", label: "Reference", minWidth: 100 },
  { id: "creationDate", label: "Created", minWidth: 100 },
  { id: "userName", label: "Owner", minWidth: 170, filter: true, accessor: (item: ITask) => item.owner.userName },
  { id: "areaName", label: "Area", minWidth: 100, filter: true, accessor: (item: ITask) => item.area?.name ?? null },
  { id: "zoneName", label: "Zone", minWidth: 100, accessor: (item: ITask) => item.zone?.name ?? null },
  { id: "priority", label: "Priority", minWidth: 80, filter: true },
  { id: "progress", label: "Progress", minWidth: 80, filter: true },
  // { id: "startDate", label: "Start Date", minWidth: 120 },
  // { id: "dueDate", label: "Due Date", minWidth: 120 },
  // { id: "approvedDate", label: "Approved Date", minWidth: 120 },
  { id: "title", label: "Title", minWidth: 400 },
  { id: "department", label: "Department", minWidth: 100, filter: true, accessor: (item: ITask) => item.department?.name ?? null },
  { id: "assigned", label: "Assigned", minWidth: 100, filter: true, accessor: (item: ITask) => item.assigned?.userName ?? null },
  // { id: "description", label: "Description", minWidth: 400 },
  // { id: "approved", label: "Approved", minWidth: 60 },
  // { id: "approvedDate", label: "Approved Date", minWidth: 100 },
  { id: "actions", label: "Actions", minWidth: 180, align: "center" },

];

const genericAccessor = (key: keyof ITask): ValueAccessor => item => item[key];

const divIndicatorContainer = {
  margin: "0.5em"
};

interface IDialogFilterData {
  key: string;
  title: string;
  open: boolean;
}

const areaX = 202;
const expandedChartsHeight = 200;
const collapseChartsHeight = 26;
const tabHeight = 50;
const px5 = 5;

const getAreas = (task: ITask[]): IOption[] => {
  const result: Record<number, IOption> = {};

  task.forEach(t => {
    if (!result[t.area?.areaID ?? 0]) {
      result[t.area?.areaID ?? 0] = { value: 3, name: t.area?.name ?? "NONE" };
    }
  });

  return Object.values(result);
};

const getFilterItems = (task: ITask[], getValue: (task: ITask) => string | null): IOption[] => {
  const result: Record<string, IOption> = {};

  task.forEach(t => {
    const value = getValue(t);
    if (value && !result[value]) {
      result[value] = { value, name: value };
    }
  });

  return Object.values(result);
};

const descendingTaskComparator = (a: any, b: any, accessor: ValueAccessor): number => {
  const vA = accessor(a);
  const vB = accessor(b);

  if (!vB || vA && vB < vA) {
    return -1;
  }

  return !vA || vB > vA ? 1 : 0;
};

export const getTaskComparator = (
  orderConfig: IOrderConfig
): (a: ITask, b: ITask) => number => {

  return orderConfig.order === "desc"
    ? (a, b) => descendingTaskComparator(a, b, orderConfig.accessor)
    : (a, b) => -descendingTaskComparator(a, b, orderConfig.accessor);
};

const Tasks = ({ user, hash, division }: IProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const height = window.innerHeight - areaX;
  const theme = useTheme();
  const [processTask] = useAsyncFetch<IEditionResponse>();
  const [processAttach] = useAsyncFetch<IEditionResponse>();
  const [orderConfig, setOrderConfig] = useState<IOrderConfig>({ order: "asc", orderBy: "creationDate", accessor: () => null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedItem, setSelectedItem] = useState<ITask | null>(null);
  const [openEditor, setOpenEditor] = useState<boolean>(false);
  const [openComments, setOpenComments] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(true);
  const [tasks, setTasks] = useState<ITask[]>([]);
  const [filter, setFilter] = useState<Record<string, IOption[]>>({
    priority: priorityFilter,
    progress: progressFilter,
    userName: [],
    department: [],
    assigned: []
  });
  const [isManager, setIsManager] = useState<boolean>(false);
  const [pieConfigByPriority, setPieConfigByPriority] = useState<IPieConfig | null>(null);
  const [pieConfigByProgress, setPieConfigByProgress] = useState<IPieConfig | null>(null);
  const [pieConfigByArea, setPieConfigByArea] = useState<IPieConfig | null>(null);
  const [pieConfigByDepartment, setPieConfigByDepartment] = useState<IPieConfig | null>(null);
  const [dialogFilterData, setDialogFilterData] = useState<IDialogFilterData>({ open: false, key: "", title: "" });
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<Record<string, string[]>>({
    priority: [],
    progress: [],
    areaName: [],
    userName: [],
    department: [],
    assigned: []
  });
  const darkMode = useSelector((state: IState) => state.darkMode);
  const { loading, data: response } = useFetch<ITask[]>(
    {
      module: "task",
      query: "getTasks",
      payload: {
        hash,
        divisionID: division.divisionID
      }
    });
  const [ getAttachments ] = useAsyncFetch<ITaskAttachment[]>();

  const dataLength = tasks ? tasks.length : 0;

  const layout: CSSProperties = {
    gridTemplateRows: "auto auto 1fr",
    alignItems: "stretch",
    height,
    maxHeight: height
  };

  const layoutTab: CSSProperties = {
    gridTemplateRows: "auto 1fr",
    alignItems: "stretch",
    // height: height - tabHeight,
    // maxHeight: height
  };

  useEffect(() => {
    setIsManager(user.permissions.find(p => p.code === "MANAGEMENT_TASK") !== undefined);
  }, [user]);

  useEffect(() => {
    if (response) {
      setTasks(response);


      setFilter({
        ...filter,
        areaName: getAreas(response),
        userName: getFilterItems(response, t => t.owner.userName),
        department: getFilterItems(response, t => t.department?.name ?? null),
        assigned: getFilterItems(response, t => t.assigned?.userName ?? null)
      });
    }
  }, [response]);

  useEffect(() => {
    const priorityGroups: Record<string, ISeries> = {};
    const progressGroups: Record<string, ISeries> = {};
    const areaGroups: Record<string, ISeries> = {};
    const departmentGroups: Record<string, ISeries> = {};

    tasks.forEach(t => {
      const priorityText = getPriorityText(t.priority);
      const progressText = getProgressText(t.progress);

      if(!priorityGroups[priorityText]) {
        priorityGroups[priorityText] = { name: priorityText, value: 1, color: getPriorityColor(t.priority) };
      } else {
        priorityGroups[priorityText].value = (priorityGroups[priorityText].value as number) + 1;
      }

      if(!progressGroups[progressText]) {
        progressGroups[progressText] = { name: progressText, value: 1, color: getProgressColor(t.progress) };
      } else {
        progressGroups[progressText].value = (progressGroups[progressText].value as number) + 1;
      }

      const areaName = t.area ? t.area.name : "NONE";
      if(!areaGroups[areaName]) {
        areaGroups[areaName] = { name: areaName, value: 1, color: t.area ? t.area.color ?? getPalette().charts.gray: getPalette().charts.gray };
      } else {
        areaGroups[areaName].value = (areaGroups[areaName].value as number) + 1;
      }

      const departmentName = t.department ? t.department.name : "NONE";
      if(!departmentGroups[departmentName]) {
        departmentGroups[departmentName] = { name: departmentName, value: 1, color: t.department ? t.department.color ?? getPalette().charts.gray: getPalette().charts.gray };
      } else {
        departmentGroups[departmentName].value = (departmentGroups[departmentName].value as number) + 1;
      }
    });

    const prioritySeries: ISeries[] = Object.values(priorityGroups);
    const progressSeries: ISeries[] = Object.values(progressGroups);
    const areaSeries: ISeries[] = Object.values(areaGroups);
    const departmentSeries: ISeries[] = Object.values(departmentGroups);


    setPieConfigByPriority({
      // width: 200,
      height: 150,
      maximum: tasks.length,
      padAngle: 0.05,
      innerRadius: 0.2,
      seriesRadius: 1.2,
      showLegend: false,
      emptyColor: getPalette().statusColor.production.background,
      title: "By Priority",
      sort: "NAME",
      series: prioritySeries,
      formatValue: n => n.toFixed(0)
    });

    setPieConfigByProgress({
      // width: 200,
      height: 150,
      maximum: tasks.length,
      padAngle: 0.05,
      innerRadius: 0.2,
      seriesRadius: 1.2,
      showLegend: false,
      emptyColor: getPalette().statusColor.production.background,
      title: "By Progress",
      sort: "NAME",
      series: progressSeries,
      formatValue: n => n.toFixed(0)
    });

    setPieConfigByArea({
      // width: 200,
      height: 150,
      maximum: tasks.length,
      padAngle: 0.05,
      innerRadius: 0.2,
      seriesRadius: 1.2,
      showLegend: false,
      emptyColor: getPalette().statusColor.production.background,
      title: "By Area",
      sort: "NAME",
      series: areaSeries,
      formatValue: n => n.toFixed(0)
    });

    setPieConfigByDepartment({
      // width: 200,
      height: 150,
      maximum: tasks.length,
      padAngle: 0.05,
      innerRadius: 0.2,
      seriesRadius: 1.2,
      showLegend: false,
      emptyColor: getPalette().statusColor.production.background,
      title: "By Department",
      sort: "NAME",
      series: departmentSeries,
      formatValue: n => n.toFixed(0)
    });

  }, [tasks]);

  const createSortHandler = (column: IColumn) => () => {
    const isAsc = orderConfig.orderBy === column.id && orderConfig.order === "asc";
    setOrderConfig({
      order: isAsc ? "desc" : "asc",
      orderBy: column.id as keyof ITask,
      accessor: column.accessor ?? genericAccessor(column.id as keyof ITask)
    });
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onAcceptEditor = (task: ITask, attachments?: ITaskAttachment[]) => {

    const saveAttachments = async (pendingFiles: ITaskAttachment[]) => {
      for (const file of pendingFiles) {
        file.taskID = task.taskID ?? 0;
        const result = await processAttach({
          module: "task",
          query: "addAttachment",
          payload: { attachment: file }
        });

        if (result?.result) {
          file.taskAttachmentID = result.editedID;
        }
      }
    };

    processTask({
      module: "task",
      query: "insertOrUpdateTask",
      payload: task
    }).then(edition => {
      if (edition && edition.result) {
        if (task.taskID) {
          if (attachments) {
            task.hasAttachments = attachments.length > 0;
          }

          setTasks(tasks.map(t => t.taskID === task.taskID ? task : t));
        } else {
          task.taskID = edition.editedID;
          setTasks([...tasks, task]);
        }

        dispatch(addAlert({ message: "The task was saved!", severity: "success", hash: undefined }));

        const pendingFiles = attachments ? attachments.filter(x => !x.taskAttachmentID) : [];
        if (pendingFiles.length > 0) {
          saveAttachments(pendingFiles).then(() => {
            dispatch(addAlert({ message: "The attachments were saved!", severity: "success", hash: undefined }));
          });
        }

        setOpenEditor(false);
        setOpenComments(false);
      } else {
        dispatch(addAlert({ message: "Something was wrong!", severity: "error", hash: undefined }));
      }
    });

  };

  const onDelete = (task: ITask) => {
    systemContext.showMessage({
      title: "Delete Task",
      color: "danger",
      message: `Do you want to delete the Task "${task.title}"?`,
      callback: () => {
        processTask({
          module: "task",
          query: "deleteTask",
          payload: { taskID: task.taskID }
        })
          .then(edition => {
            if (edition?.result) {
              dispatch(addAlert({ message: "The task was deleted!", severity: "error", hash: undefined }));
              setTasks(tasks.filter(t => t.taskID !== task.taskID));
            }
          });
      }
    });
  };

  // const onStartTask = (task: ITask) => {
  //   systemContext.showMessage(
  //     {
  //       title: "Start Task",
  //       color: "info",
  //       message: `Do you want to start the Task "${task.title}"?`,
  //       callback: () => {
  //         processTask({
  //           module: "task",
  //           query: "startTask",
  //           payload: { taskID: task.taskID }
  //         })
  //           .then(edition => {
  //             if (edition?.result) {
  //               dispatch(addAlert({ message: "The task was updated!", severity: "success", hash: undefined }));
  //             }
  //           });
  //       }
  //     }
  //   ));
  // };

  const onComplete = (task: ITask) => {
    systemContext.showMessage({
        title: "Approve Task",
        color: "info",
        message: `Do you want to Complete the Task "${task.title}"?`,
        callback: () => {
          processTask({
            module: "task",
            query: "completeTask",
            payload: { taskID: task.taskID }
          })
            .then(edition => {
              if (edition?.result) {
                task.progress = ProgressType.Completed;
                task.completeDate = DateTime.now().toFormat("yyyy-MM-dd");
                dispatch(addAlert({ message: "The task was updated!", severity: "success", hash: undefined }));
              }
            });
        }
      });
  };

  const onApprove = (task: ITask) => {
    systemContext.showMessage({
      title: "Approve Task",
      color: "info",
      message: `Do you want to Approve the Task "${task.title}"?`,
      callback: () => {
        processTask({
          module: "task",
          query: "approveTask",
          payload: { taskID: task.taskID }
        })
          .then(edition => {
            if (edition?.result) {
              dispatch(addAlert({ message: "The task was updated!", severity: "success", hash: undefined }));
            }
          });
      }
    });
  };

  const onAddTask = () => {
    setSelectedItem({
      division,
      owner: {
        userID: user.userID,
        userName: user.fullName,
        email: user.email
      },
      priority: PriorityType.Low,
      controlPriority: PriorityType.Low,
      progress: ProgressType.Pending,
      reference: "",
      title: "",
      description: "",
      creationDate: DateTime.now().toFormat("yyyy-MM-dd"),
      startDate: null,
      dueDate: null,
      completeDate: null,
      approvedDate: null,
      area: null,
      zone: null,
      department: null,
      assigned: null,
      extraData: {},
      hasAttachments: false,
      sortIndex: 999,
    });

    setOpenEditor(true);
  };

  const onEditTask = (task: ITask) => {
    setSelectedItem(cloneObject(task));
    setOpenEditor(true);
  };

  const onEditComments = (task: ITask) => {
    setSelectedItem(cloneObject(task));
    setOpenComments(true);
  };

  const onDownloadFiles = (task: ITask) => {
    getAttachments({
      module: "task",
      query: "getAttachments",
      payload: {
        taskID: task.taskID
      }
    }).then(attachments => {
      if (!attachments) {
        return;
      }

      const zip = new JSZip();

      for (const item of attachments) {
        const base64result = item.data.split(",")[1];
        zip.file(item.fileName, base64result, { base64: true });
      }

      zip.generateAsync({ type: "base64" }).then((base64) => {
        const elem = window.document.createElement("a");
        elem.href = "data:application/zip;base64," + base64;
        elem.download = "files.zip";
        elem.style.display = "none";
        (document.body || document.documentElement).appendChild(elem);
        if (typeof elem.click === "function") {
          elem.click();
        } else {
          elem.target = "_blank";
          elem.dispatchEvent(new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: true
          }));
        }

        URL.revokeObjectURL(elem.href);
        elem.remove();
      });
    });
  };

  const handleTabChange = (_event: ChangeEvent<any>, newValue: number) => {
    setTab(newValue);
  };

  const onHandleFilterAccept = (selected: any[]) => {
    setCurrentFilter({ ...currentFilter, [dialogFilterData.key]: selected });
    setDialogFilterData({ ...dialogFilterData, open: false });
  };

  const processFilter = (task: ITask): boolean => {
    let result = true;


    if (currentFilter.priority.length > 0) {
      result = currentFilter.priority.find(x => x === getPriorityText(task.priority)) !== undefined;
    }

    if (result && currentFilter.progress.length > 0) {
      result = currentFilter.progress.find(x => x === getProgressText(task.progress)) !== undefined;
    }

    if (result && currentFilter.areaName.length > 0) {
      result = currentFilter.areaName.find(x => x === task.area?.name || x === "NONE" && !task.area?.name) !== undefined;
    }

    if (result && currentFilter.userName.length > 0) {
      result = currentFilter.userName.find(x => x === task.owner.userName) !== undefined;
    }

    if (result && currentFilter.department.length > 0) {
      result = currentFilter.department.find(x => x === task.department?.name) !== undefined;
    }

    if (result && currentFilter.assigned.length > 0) {
      result = currentFilter.assigned.find(x => x === task.assigned?.userName) !== undefined;
    }

    return result;
  };

  return (
    <div className="m-1 display-grid" style={layout} >
      <Paper className="mb-1" style={{ height: open ? expandedChartsHeight : collapseChartsHeight, position: "relative"  }}>
        <div className="display-flex-row align-items-center" style={{ position: "absolute", left: 0, top: 0 }}>
          <IconButton onClick={() => setOpen(!open)} size={"small"} className="mr-1">
            {open
              ? <ExpandLessIcon fontSize="inherit" fontWeight="bold"/>
              : <ExpandMoreIcon fontSize="inherit" fontWeight="bold"/>
            }
          </IconButton>

          {!open && <strong>Charts</strong>}
        </div>
        {open &&
          <div style={divIndicatorContainer} className="display-flex-row justify-content-center">
            {pieConfigByPriority && <PieChart pieConfig={pieConfigByPriority}/>}
            {pieConfigByProgress && <PieChart pieConfig={pieConfigByProgress}/>}
            {pieConfigByArea && <PieChart pieConfig={pieConfigByArea}/>}
            {pieConfigByDepartment && <PieChart pieConfig={pieConfigByDepartment}/>}
          </div>
        }
      </Paper>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        centered={true}
        style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
        <Tab label="Tasks"/>
        {/*{isManager && <Tab label="Control"/>}*/}
      </Tabs>

      <TabPanel value={tab} index={0}>
        <div className="display-grid" style={layoutTab} >
          <div>
            <Button onClick={onAddTask} variant="contained" color="primary" startIcon={<Add/>} className="m-1">
              New Task
            </Button>
            <FormControlLabel
              control={<Switch checked={showAll} onChange={() => setShowAll(!showAll)} />}
              label="Show All" />
          </div>

          <TableContainer sx={{ overflowX: "hidden", maxHeight: height - (open ? expandedChartsHeight : collapseChartsHeight) - tabHeight * 3 - px5 }}>
            <Table stickyHeader={true} size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) =>
                    <TableCell
                      className="table-header"
                      key={`${column.id}-${index}`}
                      align={column.align}
                      style={{ minWidth: column.minWidth, width: column.minWidth }}
                      sortDirection={orderConfig.orderBy === column.id ? orderConfig.order : false}>
                      {column.id !== "actions"
                        ? <div className="display-flex-row">
                          {column.filter &&
                            <Tooltip
                              title="Filter">
                              <IconButton onClick={() => setDialogFilterData({
                                open: true,
                                key: column.id,
                                title: column.label as string
                              })}>
                                <FilterList fontSize="small" color={currentFilter[column.id] && currentFilter[column.id].length > 0 ? "secondary" : "inherit"}/>
                              </IconButton>
                            </Tooltip>
                          }
                          <TableSortLabel
                            active={orderConfig.orderBy === column.id}
                            direction={orderConfig.orderBy === column.id ? orderConfig.order : "asc"}
                            onClick={createSortHandler(column)}>
                            {column.label}
                            {orderConfig.orderBy === column.id
                              ? <span className="cell-visuallyHidden">
                                {orderConfig.order === "desc" ? "sorted descending" : "sorted ascending"}
                              </span>
                              : null}
                          </TableSortLabel>
                        </div>
                        : column.label
                      }
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody style={{ background: theme.palette.background.paper }}>

                {loading &&
                  <StyledTableRow>
                    <TableCell colSpan={12}>
                      <CircularProgress />
                    </TableCell>
                  </StyledTableRow>
                }

                {tasks && stableSort<ITask>(tasks.filter(x => (showAll || x.owner.userID === user.userID) && processFilter(x) ), getTaskComparator(orderConfig))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: ITask, index: number) => {
                    return (
                      <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`prod-eaf-row-${index}`}
                        onClick={() => setSelectedItem(row)} selected={row === selectedItem} onDoubleClick={() => onEditTask(row)}>
                        {columns.map((column: IColumn) => {

                          if (column.id === "index") {
                            return (
                              <TableCell key={`${column.id}-${index}`} align="right" >
                                {index + 1}
                              </TableCell>
                            );
                          }

                          if (column.id === "actions") {
                            const owner = row.owner.userID === user.userID;
                            const developer = row.assigned && row.assigned.userID === user.userID;

                            return (
                              <TableCell key={`${column.id}-${index}`} align="right">
                                {(isManager || owner) && !row.approvedDate && row.progress === ProgressType.Completed &&
                                  <Tooltip title="Approve Task">
                                    <IconButton size={"small"} onClick={() => onApprove(row)} color="success">
                                      <ThumbUpAltIcon/>
                                    </IconButton>
                                  </Tooltip>
                                }

                                {developer && !row.completeDate &&
                                  <Tooltip title="Complete Task">
                                    <IconButton size={"small"} onClick={() => onComplete(row)} color="success">
                                      <TaskAltIcon/>
                                    </IconButton>
                                  </Tooltip>
                                }

                                {row.hasAttachments &&
                                  <Tooltip title="Attachments">
                                    <IconButton size={"small"} onClick={() => onDownloadFiles(row)}>
                                      <AttachFileIcon/>
                                    </IconButton>
                                  </Tooltip>
                                }

                                {(isManager || owner) && !(row.approvedDate !== null) &&
                                  <Tooltip title="Edit Task">
                                    <IconButton size={"small"} onClick={() => onEditTask(row)} color="info">
                                      <EditIcon/>
                                    </IconButton>
                                  </Tooltip>
                                }

                                <Tooltip title="Comments">
                                  <IconButton size={"small"} onClick={() => onEditComments(row)} >
                                    { !row.extraData.comments || row.extraData.comments.length === 0 ? <ModeCommentIcon/> : <CommentIcon/>}
                                  </IconButton>
                                </Tooltip>

                                {/*{isManager && !row.approvedDate !== null && row.startDate === null &&*/}
                                {/*  <Tooltip title="Start Task">*/}
                                {/*    <IconButton size={"small"} onClick={() => onStartTask(row)} color="success">*/}
                                {/*      <PlayArrowIcon/>*/}
                                {/*    </IconButton>*/}
                                {/*  </Tooltip>*/}
                                {/*}*/}
                                {(isManager || owner) && !(row.approvedDate !== null) &&
                                  <Tooltip title="Delete Task">
                                    <IconButton size={"small"} onClick={() => onDelete(row)} color="error">
                                      <Delete/>
                                    </IconButton>
                                  </Tooltip>
                                }
                              </TableCell>);
                          }

                          const value = row[column.id as keyof ITask] as BaseType;

                          if (column.id === "progress") {
                            const progress = value as ProgressType;

                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                <Tooltip title={getProgressText(progress)}>
                                  <span>
                                    <ProgressIcon progress={progress} />
                                  </span>
                                </Tooltip>
                              </TableCell>);
                          }

                          if (column.id === "priority") {
                            const priority = value as PriorityType;

                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                <Tooltip title={getPriorityText(priority)}>
                                  <span>
                                    <PriorityIcon priority={priority} />
                                  </span>
                                </Tooltip>
                              </TableCell>);
                          }

                          if (column.id === "areaName") {
                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                {row.area ? row.area.name : ""}
                              </TableCell>);
                          }

                          if (column.id === "zoneName") {
                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                {row.zone ? row.zone.name : ""}
                              </TableCell>);
                          }

                          if (column.id === "department") {
                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                {row.department ? row.department.name : ""}
                              </TableCell>);
                          }

                          if (column.id === "userName") {
                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                {row.owner.userName}
                              </TableCell>);
                          }

                          if (column.id === "assigned") {
                            return (
                              <TableCell key={`${column.id}-${index}`} align={column.align}>
                                {row.assigned ? row.assigned.userName : ""}
                              </TableCell>);
                          }

                          return (
                            <TableCell key={`${column.id}-${index}`} align={column.align}>
                              {column.format && typeof value === "number" ? column.format(value) : value}
                            </TableCell>
                          );
                        })}
                      </StyledTableRow>
                    );
                  })}
              </TableBody>
            </Table>
            {!tasks && loading &&
              <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2">
                <CircularProgress/>
              </Paper>
            }

            {openEditor && selectedItem &&
              <TaskEditor task={selectedItem} onClose={() => setOpenEditor(false)} onAccept={onAcceptEditor} isManager={isManager}/>
            }
            { openComments && selectedItem &&
              <TaskComments task={selectedItem} onClose={() => setOpenComments(false)}
                updateTask={t => onAcceptEditor(t)} user={user} isManager={isManager}/>
            }
          </TableContainer>

          <Table>
            <TableBody>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
                  colSpan={columns.length + 1}
                  count={dataLength}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableBody>
          </Table>

          { dialogFilterData.open &&
            <FilterDialog open={dialogFilterData.open}
              handleClose={() => setDialogFilterData({ ...dialogFilterData, open: false })}
              onHandleAccept={onHandleFilterAccept} property={dialogFilterData.title}
              options={filter[dialogFilterData.key].map(x => x.name)} currentFilter={currentFilter[dialogFilterData.key]}/> }
        </div>
      </TabPanel>
    </div>
  );
};

export default Tasks;
